/**
 * Operations — co-located typed client for the read-only operations routes
 * (P5 Wave 4 contract §3, backend `operations.js` → mounted at
 * `/api/admin/quill/operations`).
 *
 * Like agents/api.ts this REPLICATES the shared `request`/`unwrap` seam from
 * services/quillAdminService.ts (those helpers are module-private; only
 * `QuillApiError` is exported) rather than editing the service. Read-only:
 * there are no mutations on this surface.
 *
 * The `operations.js` backend is built by the orchestrator against the same
 * frozen contract; several rollup fields there are described loosely, so every
 * response type below is intentionally defensive (optional fields, numbers that
 * may arrive as node-pg bigint strings) and the page coerces at the use site.
 */

import { QuillApiError } from '../../../services/quillAdminService';

const BASE = '/api/admin/quill';

function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }
  return (body ?? {}) as T;
}

function unwrap<T>(body: unknown): T {
  const b = body as { data?: T } | null | undefined;
  if (b && typeof b === 'object' && 'data' in b && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

// ════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════

export type CountLike = number | string | null | undefined;

/** CHECK-constrained status of a `quill_job_runs` row. */
export type JobStatus = 'ok' | 'partial' | 'failed' | 'running';

/** One row of GET /operations/jobs — a `quill_job_runs` record (migration 529). */
export interface JobRun {
  id: CountLike;
  job_name: string;
  status: JobStatus | string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: CountLike;
  scope_total: CountLike;
  scope_done: CountLike;
  error_code: string | null;
  error_message: string | null;
  created_at: string | null;
}

/** One row of GET /operations/sync-log — a `quill_xai_models_sync_log` record.
 *  Columns match operations.js exactly: the log carries `started_at`/`finished_at`
 *  (there is NO `synced_at`/`created_at` column). */
export interface SyncLogRow {
  id?: CountLike;
  slot?: string | null;
  status?: string | null;
  triggered_by?: string | null;
  actor_email?: string | null;
  models_found?: CountLike;
  models_inserted?: CountLike;
  models_updated?: CountLike;
  models_deprecated?: CountLike;
  duration_ms?: CountLike;
  error_code?: string | null;
  error_message?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  [key: string]: unknown;
}

/** One day of GET /operations/guardrail-rate — block/allow counts for the sparkline. */
export interface GuardrailRatePoint {
  day: string;
  blocked: CountLike;
  allowed: CountLike;
  total?: CountLike;
  [key: string]: unknown;
}

/** GET /operations/summary — the rollup returned under `data` by operations.js.
 *  Field names are pinned to what the backend actually returns:
 *  `{ last_nightly, catalog:{last_synced_at, active_models}, guardrail:{window_days,
 *  total, blocked, block_rate}, pending_reverifications, degraded }`. */
export interface OperationsSummary {
  /** Last nightly persona-recompile run (quill_job_runs). */
  last_nightly?: {
    job_name?: string;
    status?: JobStatus | string;
    started_at?: string | null;
    finished_at?: string | null;
    duration_ms?: CountLike;
    scope_done?: CountLike;
    scope_total?: CountLike;
  } | null;
  /** Catalog (xAI model sync) freshness. The `status`/`stale`/`sync_age_hours`
   *  fields are only populated by the page's sync-log fallback, not the backend. */
  catalog?: {
    last_synced_at?: string | null;
    active_models?: CountLike;
    status?: string | null;
    stale?: boolean;
    sync_age_hours?: CountLike;
  } | null;
  /** Guardrail block-rate rollup over the trailing window (7 days server-side). */
  guardrail?: {
    window_days?: CountLike;
    total?: CountLike;
    blocked?: CountLike;
    block_rate?: CountLike;
  } | null;
  /** Outstanding (unacknowledged) safety re-verification reminders. */
  pending_reverifications?: CountLike;
  /** Names of sources that degraded (prod-migration-lag) on this read. */
  degraded?: string[];
  [key: string]: unknown;
}

// ════════════════════════════════════════════════════════════════════════
// Client (read-only)
// ════════════════════════════════════════════════════════════════════════

export const operationsApi = {
  /** GET /operations/jobs?limit= — recent quill_job_runs rows. */
  async getJobs(limit?: number): Promise<JobRun[]> {
    const body = await request<{ success: true; data: JobRun[] }>(`/operations/jobs${qs({ limit })}`);
    return unwrap<JobRun[]>(body);
  },

  /** GET /operations/sync-log — recent catalog-sync rows. */
  async getSyncLog(): Promise<SyncLogRow[]> {
    const body = await request<{ success: true; data: SyncLogRow[] }>('/operations/sync-log');
    return unwrap<SyncLogRow[]>(body);
  },

  /** GET /operations/guardrail-rate?days= — per-day block/allow counts. */
  async getGuardrailRate(days?: number): Promise<GuardrailRatePoint[]> {
    const body = await request<{ success: true; data: GuardrailRatePoint[] }>(
      `/operations/guardrail-rate${qs({ days })}`
    );
    return unwrap<GuardrailRatePoint[]>(body);
  },

  /** GET /operations/summary — the metric-card rollup (FLAT or enveloped). */
  async getSummary(): Promise<OperationsSummary> {
    const body = await request<OperationsSummary>('/operations/summary');
    return unwrap<OperationsSummary>(body);
  },
};

export default operationsApi;
